// Admin User Management — TanStack Query hook for GET /api/v1/admin/users/:id.
//
// Lazily loaded by UserDetailDialog: `enabled` only fires once a user id
// is selected, so opening the list page issues no detail fetch.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type UserSession = {
  id: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  revoked_at: string | null;
};

export type UserDetail = {
  id: string;
  email: string;
  email_verified: boolean;
  created_at: string;
  last_sign_in_at: string | null;
  is_admin: boolean;
  display_name: string | null;
  handle: string | null;
  region: string | null;
  language: string | null;
  locale: string | null;
  bio: string | null;
  first_name: string | null;
  last_name: string | null;
  nationality: string | null;
  profile_visibility: string;
  creator_status: string;
  kyc_tier: string;
  is_blocked: boolean;
  blocked_at: string | null;
  blocked_until: string | null;
  block_reason: string | null;
  blocked_by: string | null;
  balance_cents: number;
  escrow_cents: number;
  cumulative_resale_cents: number;
  collection_count: number;
  sealed_dossier_count: number;
  active_share_listings: number;
  active_sealed_listings: number;
  recent_sessions: UserSession[];
};

export type AdminUserDetailResponse = {
  data: UserDetail;
};

async function fetchAdminUserDetail(id: string): Promise<AdminUserDetailResponse> {
  const response = await fetch(`/api/v1/admin/users/${id}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin user fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminUserDetailResponse;
}

export function useAdminUserDetail(userId: string | null) {
  return useQuery<AdminUserDetailResponse, Error>({
    queryKey: ["admin-user-detail", userId],
    queryFn: () => fetchAdminUserDetail(userId as string),
    enabled: userId !== null,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}
